import React from "react";
import type { ExtendedChordQuality } from "../../lib/chordEngine";
import { getChordDef, getQualitiesByCategory } from "../../lib/chordEngine";

export interface ChordCategoryChip {
  id: string;
  label: string;
}

export const CHORD_CATEGORY_CHIPS: ChordCategoryChip[] = [
  { id: "triada", label: "Tríada" },
  { id: "tetrada", label: "7ª" },
  { id: "tension", label: "Tensión" },
  { id: "alterada", label: "Alterada" },
];

/** Atajos de estilo — eligen una calidad representativa. */
export const CHORD_STYLE_SHORTCUTS: {
  id: string;
  label: string;
  category: string;
  quality: ExtendedChordQuality;
}[] = [
  { id: "pop", label: "Pop", category: "triada", quality: "major" },
  { id: "jazz", label: "Jazz", category: "tetrada", quality: "maj7" },
  { id: "blues", label: "Blues", category: "tetrada", quality: "7" },
  { id: "soul", label: "Soul", category: "tension", quality: "m9" },
  { id: "film", label: "Cine", category: "alterada", quality: "maj9" },
];

export interface ChordSpeciesStripProps {
  activeCategory: string;
  quality: ExtendedChordQuality;
  onCategoryChange: (categoryId: string) => void;
  onQualityChange: (q: ExtendedChordQuality) => void;
  onStyleShortcut?: (categoryId: string, quality: ExtendedChordQuality) => void;
}

export const ChordSpeciesStrip: React.FC<ChordSpeciesStripProps> = ({
  activeCategory,
  quality,
  onCategoryChange,
  onQualityChange,
  onStyleShortcut,
}) => {
  const qualitiesInCat = getQualitiesByCategory(activeCategory);

  return (
    <div className="w-full shrink-0 flex flex-col gap-2 px-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[9px] font-sans font-semibold uppercase tracking-wider text-slate-500 mr-1">
          Especie
        </span>
        {CHORD_CATEGORY_CHIPS.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => onCategoryChange(cat.id)}
            className={`h-7 px-2.5 rounded-lg text-[10px] font-sans font-medium transition-all ${
              activeCategory === cat.id
                ? "bg-[var(--bordona-accent)] text-white"
                : "bg-white/[0.07] text-slate-300 hover:bg-white/12"
            }`}
          >
            {cat.label}
          </button>
        ))}
        <span className="w-px h-5 bg-white/10 mx-0.5" aria-hidden />
        {CHORD_STYLE_SHORTCUTS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              onCategoryChange(s.category);
              onQualityChange(s.quality);
              onStyleShortcut?.(s.category, s.quality);
            }}
            className={`h-7 px-2 rounded-lg text-[10px] font-sans transition-all border ${
              quality === s.quality
                ? "border-[var(--bordona-accent-border)] bg-[var(--bordona-accent-soft)] text-[var(--bordona-accent)]"
                : "border-white/10 bg-white/[0.05] text-slate-400 hover:bg-white/10"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-1 max-h-[52px] overflow-y-auto scrollbar-thin">
        {qualitiesInCat.map((q) => {
          const qDef = getChordDef(q);
          return (
            <button
              key={q}
              type="button"
              onClick={() => onQualityChange(q)}
              className={`px-2 py-1 rounded-md text-[10px] font-sans transition-all ${
                quality === q
                  ? "bg-white/15 text-slate-100 ring-1 ring-white/20"
                  : "bg-white/[0.05] text-slate-400 hover:bg-white/10 hover:text-slate-200"
              }`}
              title={qDef?.degreeNames.join(" · ")}
            >
              {qDef?.label || q}
            </button>
          );
        })}
      </div>
    </div>
  );
};
